import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';

export class FloatingCommentsHelper {
  static getComments(comments: Comment[], pageIds: string[]) {
    return comments
      .filter(FloatingCommentsHelper.isCommentVisible)
      .sort(FloatingCommentsHelper.sortCommentsByEditorTextSelection)
      .sort((a, b) => FloatingCommentsHelper.sortCommentsByPageOrder(a, b, pageIds));
  }

  static isCommentVisible({ editorSelection }: Comment) {
    return !!editorSelection;
  }

  static getTotalHeight(element: HTMLElement): number {
    const height = element.offsetHeight;
    const marginTop = +element.style.marginTop.replace('px', '');
    return height + marginTop;
  }

  static getMarginToRemoveFromComment(
    commentMargin: number,
    marginToRemove: number,
    minimumMargin: number
  ): number {
    const diff = commentMargin - marginToRemove;
    if (diff >= minimumMargin) {
      return marginToRemove;
    }
    return commentMargin - minimumMargin;
  }

  static getReversedCommentIdsBeforeId(id: string, comments: Comment[]): string[] {
    const index = comments.findIndex((comment: Comment) => comment.id === id);
    return comments
      .map((comment: Comment) => comment.id)
      .slice(0, index)
      .reverse();
  }

  static getCommentIdsAfterSelectedComment(id: string, comments: Comment[]): string[] {
    const index = comments.findIndex((comment: Comment) => comment.id === id);
    return comments.map((comment: Comment) => comment.id).slice(index + 1);
  }

  static evaluateIsIndexFinalComment(index: number, commentsLength: number): boolean {
    return index + 1 === commentsLength;
  }

  static evaluateMarginOptions(actual: number, selection?: number): number {
    if (selection > 0 || selection < 0) {
      return selection;
    }
    return actual;
  }

  static willCommentBePushedDown(futureDistance: number, desiredDistance: number): boolean {
    return futureDistance > desiredDistance;
  }

  static sortCommentsByEditorTextSelection(a: Comment, b: Comment): number {
    if (!a.editorSelection?.textSelection || !b.editorSelection?.textSelection) {
      return 0;
    }
    const aTextSelection = a.editorSelection.textSelection;
    const aFrom =
      aTextSelection.anchor < aTextSelection.head ? aTextSelection.anchor : aTextSelection.head;
    const bTextSelection = b.editorSelection.textSelection;
    const bFrom =
      bTextSelection.anchor < bTextSelection.head ? bTextSelection.anchor : bTextSelection.head;
    return aFrom - bFrom;
  }

  static sortCommentsByPageOrder(a: Comment, b: Comment, pageIds: string[]): number {
    const aPagePosition = pageIds.findIndex(id => id === a.pageId);
    const bPagePosition = pageIds.findIndex(id => id === b.pageId);
    if (aPagePosition < bPagePosition) {
      return -1;
    }
    if (aPagePosition > bPagePosition) {
      return 1;
    }
    return 0;
  }

  static canDecreaseMargin(selectionMargin: number, minimumMargin: number): boolean {
    return selectionMargin > minimumMargin;
  }

  static decreaseMargin(
    selectionMargin: number,
    desiredDistance: number,
    futureDistance: number,
    minimumMargin: number
  ): number {
    const distanceFromDesired = futureDistance - desiredDistance;
    const resultingMargin = selectionMargin - distanceFromDesired;
    if (resultingMargin < minimumMargin) {
      return minimumMargin;
    }
    return resultingMargin;
  }
}
